import React, { useEffect } from 'react';
import { GetServerSideProps, Redirect } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { Result } from '@commercetools/frontend-domain-types/product/Result';
import { AcceptedQueryTypes } from '@commercetools/frontend-sdk/lib/types/Query';
import ContentstackLivePreview from '@contentstack/live-preview-utils';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { BREAD_SCRIPT_URL } from 'helpers/constants/bread';
import useDayjsLocale from 'helpers/hooks/useDayjsLocale';
// import { useFormat } from 'helpers/hooks/useFormat'; // Currently unused
import { NikonInteractiveModel } from 'helpers/nikonInteractiveModel/nikonInteractiveModel';
import { getDomainName } from 'helpers/utils/getDomainName';
import { stripLocalityCodeFromUrl } from 'helpers/utils/localityCodeUtils';
import { setMaxAge } from 'helpers/utils/setMaxAge';
import { getLocalizationInfo } from 'project.config';
import { sdk } from 'sdk';
import { createClient, PageDataResponse, ResponseError } from 'frontastic';
import { FrontasticRenderer } from 'frontastic/lib/renderer';
import { tastics } from 'frontastic/tastics';
import InternalServerError from './500';
import styles from './slug.module.css';
import GTMSnippet from '../components/headless/GTMSnippet';
import { PriceSpiderHead } from '../components/headless/PriceSpiderSnippet';
import TurnToSnippet from '../components/headless/TurnToSnippet';
import { Log } from '../helpers/errorLogger';

ContentstackLivePreview.init();

type SlugProps = {
  // This needs an overhaul. Can be too many things in my opinion (*Marcel)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  // data: RedirectResponse | PageDataResponse | ResponseError | { ok: string; message: string } | string;
  locale: string;
};

export default function Slug({ data, locale }: SlugProps) {
  // const { formatMessage } = useFormat({ name: 'common' }); // Currently unused
  const router = useRouter();
  const productSkuId = router.asPath?.split('/p/')[1]?.split('/')?.[1] || '';
  useDayjsLocale();

  console.log('localelocalelocale - slug ==>', locale);

  // Handle URL localization cleanup using the centralized utility
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const currentUrl = new URL(window.location.href);
      const cleanUrl = stripLocalityCodeFromUrl(currentUrl);
      if (cleanUrl) {
        history.replaceState({ ...window.history.state, as: cleanUrl, url: cleanUrl }, '', cleanUrl);
      }

      // Note: Link cleanup is now handled at the component level using useCleanLocaleLinks hook
      // Footer links are cleaned by the Footer component
      // Cookie banner links are cleaned by the CookieBannerLinkCleaner component in _app.tsx
      return () => {};
    }

    return () => {};
  }, []);

  // The frontastic-session cookie is set by the system and may not be immediately available
  // This function will poll for the cookie until it is available or the maximum number of attempts is reached.
  // This is necessary to ensure that the Nikon Interactive model can be initialized with the session cookie
  // We expect the cookie to be set by the Frontastic system and will wait for it to be available
  // default interval is 100ms and max attempts is 50, which gives a maximum wait time of 5 seconds
  // This should be sufficient for the cookie to be set by the system, but can be adjusted if necessary
  const getCookie = (name: string, interval = 100, maxAttempts = 50): Promise<string> => {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const checkCookie = () => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        const sess = parts.pop()?.split(';')?.shift() || null;

        if (sess) {
          resolve(sess);
        } else if (attempts >= maxAttempts) {
          reject(false);
        } else {
          attempts++;
          setTimeout(checkCookie, interval);
        }
      };
      checkCookie();
    });
  };
  // add extension runner id for use by sposto in free entry code
  useEffect(() => {
    getCookie('frontastic-session').then((cookieValue) => {
      if (!cookieValue) {
        console.error(`frontastic-session cookie not found after max attempts. Pleaae check the Frontastic setup.`);
        return;
      }

      const host: any = process.env.NEXT_PUBLIC_FRONTASTIC_HOST;
      sessionStorage.setItem('frontastic-host', btoa(host));

      const loc: string = getLocalizationInfo(locale)?.locale || 'en_US@USD';
      sessionStorage.setItem('frontastic-locale', btoa(loc));

      const frontasticExtension: any = process.env.NEXT_PUBLIC_EXT_BUILD_ID;
      sessionStorage.setItem('frontastic-extension', btoa(frontasticExtension));

      const breadKey: any = process.env.NEXT_PUBLIC_BREAD_INTEGRATION_KEY || null;
      sessionStorage.setItem('bread-key', btoa(breadKey));

      const breadUrl: any = process.env.NEXT_PUBLIC_BREAD_SCRIPT_URL || null;
      sessionStorage.setItem('bread-url', btoa(breadUrl));

      const session = cookieValue ?? '';
      sessionStorage.setItem('frontastic-session', btoa(session));

      const namespace = 'nikonInteractive';
      const NikonInteractive = (window[namespace] ||= new NikonInteractiveModel({
        host,
        session,
        locale: loc,
        extension: frontasticExtension,
        breadKey,
        breadUrl,
      }));

      NikonInteractive.initialize(router.asPath);
    });
  }, [locale, router.asPath]);

  if (!data || typeof data === 'string') {
    return <InternalServerError />;
  }

  if (!data?.ok && data?.message) {
    return <InternalServerError />;
  }

  const domain = locale ? getDomainName(locale) : '';
  const cleanedPath = router.asPath.split('?')[0].split('#')[0];

  // If the url path is a product page, and it doesn't end with /overview, add /overview to the url path
  // For example, Buy Now pages should have canonical url pointing to the pdp page
  const isNotOverview = cleanedPath?.startsWith('/p/') && !cleanedPath?.endsWith('/overview');
  const canonicalUrl = domain ? `${domain}${cleanedPath}${isNotOverview ? '/overview' : ''}` : null;

  return (
    <>
      {/* <MetaDataComponent
        content={{
          meta_title: formatMessage({
            id: 'meta.title',
            defaultMessage: 'Cameras and photography equipment from Nikon',
          }),
          meta_description: formatMessage({
            id: 'meta.desc',
            defaultMessage: 'Find high quality cameras and photography accessories at Nikon',
          }),
        }}
        tasticName="SLUG"
      /> */}
      <Head>
        {<GTMSnippet locale={router.locale} />}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        {productSkuId !== '' ? <PriceSpiderHead /> : ''}

        {/* https://swiftype.com/documentation/site-search/crawler-troubleshooting#removing-documents */}
        {isNotOverview && <meta name="st:robots" content="noindex" />}
      </Head>
      {productSkuId !== '' ? <TurnToSnippet sku={productSkuId} /> : ''}

      <FrontasticRenderer data={data} tastics={tastics} wrapperClassName={styles.gridWrapper} />
      <Script src={BREAD_SCRIPT_URL} />
    </>
  );
}

// Helper function to create error page props
const createErrorPageProps = async (
  errorType: 'article' | 'page',
  req: any,
  res: any,
  resolvedUrl: string,
  categories: any,
  actualLocale: string,
) => {
  res.statusCode = 404;

  const protocol = req.headers.referer?.split('://')[0] || 'https';
  const serverUrl = `${protocol}://${req.headers.host}${resolvedUrl}`;
  const origin = `${protocol}://${req.headers.host}`;

  // Try to fetch error page data from ContentStack for article errors
  let errorPageData = null;
  if (errorType === 'article') {
    try {
      const errorPageResponse = await sdk.callAction({
        actionName: 'contentstack/getErrorPage',
        query: { limit: 1 },
      });
      errorPageData =
        !errorPageResponse.isError &&
        errorPageResponse.data &&
        Array.isArray(errorPageResponse.data) &&
        errorPageResponse.data.length > 0
          ? errorPageResponse.data[0]
          : null;
    } catch (error) {
      console.warn('[L&E 404] Failed to fetch error page data:', error);
    }
  }

  return {
    props: {
      data: {
        error: errorType === 'article' ? 'Article not found' : 'Page not found',
        showErrorPage: true,
        skipErrorPageApiCall: true,
        errorPageData,
        categories,
        serverUrl,
        serverState: null,
        origin,
      },
      origin: req.headers?.origin || null,
      locale: actualLocale,
      ...(await serverSideTranslations(actualLocale, COMMON_TRANSLATION_NAMESPACES)),
    },
  };
};

const isNotFoundPage = (data: any) => {
  return data.data?.dataSources?.__master?.payload?.reason === 'NotFound';
};

function isNonExistentCategory(data: PageDataResponse, categories: Result) {
  /* All paths without a prefix are considered category product listings.
     In some special cases (e.g. /cameras-on-sale, /refurbished-cameras) an additional
     datasource is configured in frontastic. This function matches the logic in
    ProductListWrapped for displaying 'not found' error to set the status code to 404
  */
  if (data.pageFolder?.pageFolderType === 'frontastic/category') {
    const dataSource = Object.values(data.data?.dataSources ?? {})[0] as { category: string };
    if (!dataSource) return true;
    const category = dataSource.category?.split('/').at(-1);
    if (!category) return false;
    return !categories.items.some((item) => item.slug === category);
  }
  return false;
}

// Common translation namespaces used across the app
const COMMON_TRANSLATION_NAMESPACES = [
  'account',
  'cart',
  'categories',
  'checkout',
  'common',
  'customer-support',
  'error',
  'learn-and-explore',
  'newsletter',
  'orders',
  'payment',
  'plp',
  'press-room-list-page',
  'product',
  'service-advisory-list',
  'search',
  'success',
  'thank-you',
  'trade-in',
  'wishlist',
  'narrow',
  'nikon-care',
];

export const getServerSideProps: GetServerSideProps | Redirect = async ({
  params,
  locale,
  query,
  req,
  res,
  resolvedUrl,
}) => {
  const maxAge = setMaxAge(req.url || '/');
  res.setHeader('Cache-Control', `max-age=${maxAge}`);

  // Use the locale detected by middleware (from x-locale header) instead of Next.js default locale
  const actualLocale = (req.headers['x-locale'] as string) || (locale as string);

  console.log(
    `[SSR] Host: ${req.headers.host}, x-locale: ${req.headers['x-locale']}, Next.js locale: ${locale}, Using: ${actualLocale}`,
  );

  sdk.configureForNext(actualLocale);
  const frontastic = createClient();

  // if LOCAL or DEV environment, use "DEVELOPMENT ONLY" api hub credentials
  // QA will use the DEVELOPMENT api hub, but not the "DEVELOPMENT ONLY" credentials
  // UAT will use the STAGING api hub (staging api hub will ignore the "DEVELOPMENT ONLY" credentials
  // PROD will use the PRODUCTION api hub (staging api hub will ignore the "DEVELOPMENT ONLY" credentials
  req.headers['api-env'] =
    // req.headers.host?.startsWith('localhost') ||
    req.headers.host?.startsWith('local.nikondev.com') ||
    req.headers.host?.startsWith('main--nikon') ||
    req.headers.host?.startsWith('dev--nikon') ||
    req.headers.host?.startsWith('nikonstore-dev') ||
    req.headers.host?.startsWith('dev.nikon') ||
    req.headers.host?.startsWith('dev-en-nikonca') ||
    req.headers.host?.startsWith('dev.en.nikon') ||
    req.headers.host?.startsWith('dev-fr-nikonca') ||
    req.headers.host?.startsWith('dev.fr.nikon')
      ? 'development'
      : '';

  let data, categories;
  try {
    [data, categories] = await Promise.all([
      frontastic.getRouteData(params?.slug as string[], query as AcceptedQueryTypes, req, res),
      frontastic.getCategories(req, res),
    ]);
  } catch (error) {
    // Development-only: Handle webpack hot-update errors gracefully
    if (process.env.NODE_ENV === 'development' && (req.url?.includes('webpack') || req.url?.includes('hot-update'))) {
      res.statusCode = 404;
      res.end();
      return { props: {} } as any;
    }
    // Re-throw for all other cases to maintain normal error handling
    throw error;
  }

  if (isNotFoundPage(data)) {
    console.log(`Not found error page detected for path: ${resolvedUrl}, setting status code to 404`);
    res.statusCode = 404;
  }

  if (isNonExistentCategory(data as PageDataResponse, categories)) {
    res.statusCode = 404;
  }

  if (data === null) {
    return {
      notFound: true,
    };
  }

  // Check for empty object which might be how null gets transformed
  if (data && typeof data === 'object' && Object.keys(data).length === 0) {
    // Check if this is a Learn & Explore article URL
    const isLNEArticleUrl =
      params?.slug &&
      params.slug[0] === 'learn-and-explore' &&
      params.slug[1] === 'c' &&
      params.slug[2] && // category slug
      params.slug[3]; // article slug

    if (isLNEArticleUrl) {
      return await createErrorPageProps('article', req, res, resolvedUrl, categories, actualLocale);
    } else {
      return {
        notFound: true,
      };
    }
  }

  // Check for not-found page type from backend - keeping for other use cases
  if (data && (data as any).pageFolder && (data as any).pageFolder.pageFolderType === 'frontastic/not-found') {
    return await createErrorPageProps('page', req, res, resolvedUrl, categories, actualLocale);
  }

  if (data) {
    if (data instanceof ResponseError && data.getStatus() == 404) {
      return {
        notFound: true,
      };
    } else if (typeof data === 'object' && 'target' in data && 'statusCode' in data) {
      return {
        redirect: {
          destination: data.target,
          statusCode: data.statusCode,
        } as Redirect,
      };
    }
  }

  if (data instanceof Error) {
    Log.error(new Error('Error retrieving data: '), data);
    throw data;
  }

  if (typeof data === 'string') {
    throw new Error(data);
  }

  const protocol = req.headers.referer?.split('://')[0] || 'https';

  const serverUrl = `${protocol}://${req.headers.host}${resolvedUrl}`;

  const serverState = null;

  const dataWithOrigin = {
    ...data,
    origin: `${protocol}://${req.headers.host}`,
  };

  return {
    props: {
      data: { ...dataWithOrigin, categories, serverUrl, serverState },
      origin: req.headers?.origin || null,
      locale: actualLocale,
      ...(await serverSideTranslations(actualLocale, [
        'account',
        'cart',
        'categories',
        'checkout',
        'common',
        'customer-support',
        'error',
        'learn-and-explore',
        'newsletter',
        'orders',
        'payment',
        'plp',
        'press-room-list-page',
        'product',
        'service-advisory-list',
        'search',
        'success',
        'thank-you',
        'wishlist',
        'narrow',
        'nikon-care',
      ])),
    },
  };
};
